import {
  ApplicationRef,
  ComponentRef,
  computed,
  createComponent,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { concatMap, firstValueFrom, map, timer } from 'rxjs';
import {
  ToasterDelay,
  CloseAnimationDelay,
  TOAST_CONFIG_TOKEN,
  ToastPositionEnum,
  IToastSettings,
  ToastTypeEnum,
  IToasterService,
} from '@aksia/infrastructure';

import { ToastComponent } from '../toast/toast.component';

type TToast = {
  id: string;
  settings: IToastSettings;
  closed: boolean;
  cmpRef?: ComponentRef<ToastComponent>;
};

@Injectable({
  providedIn: 'root',
})
export class ToasterService implements IToasterService {
  //#region Injections

  config = inject(TOAST_CONFIG_TOKEN, { optional: true });
  appRef = inject(ApplicationRef);

  //#endregion

  //#region Properties

  private _toasts = signal<TToast[]>([]);
  toasts = computed(() => this._toasts());

  //#endregion

  //#region Toast Methods

  success(
    message: string,
    options?: Partial<Omit<IToastSettings, 'type' | 'message'>>,
  ) {
    this.showToast({ type: ToastTypeEnum.Success, message, ...options });
  }

  error(
    message: string,
    options?: Partial<Omit<IToastSettings, 'type' | 'message'>>,
  ) {
    this.showToast({ type: ToastTypeEnum.Error, message, ...options });
  }

  info(
    message: string,
    options?: Partial<Omit<IToastSettings, 'type' | 'message'>>,
  ) {
    this.showToast({ type: ToastTypeEnum.Info, message, ...options });
  }

  warning(
    message: string,
    options?: Partial<Omit<IToastSettings, 'type' | 'message'>>,
  ) {
    this.showToast({ type: ToastTypeEnum.Warning, message, ...options });
  }

  // #endregion

  // #region Helpers

  private createNewId(): string {
    return Math.random().toString().substring(2);
  }

  private markToastAsClosed(id: string) {
    this._toasts.update((toasts) => {
      toasts.forEach((toast) => {
        if (toast.id == id) {
          toast.closed = true;
          if (toast.cmpRef) {
            toast.cmpRef.location.nativeElement.classList.add('closing');
          }
          return;
        }
      });
      return [...toasts];
    });
  }

  private removeToastById(id: string) {
    const toast = this._toasts().find((toast) => toast.id == id);

    if (toast?.cmpRef) {
      this.appRef.detachView(toast.cmpRef.hostView);
      toast.cmpRef.destroy();
    }

    this._toasts.update((toasts) => toasts.filter((toast) => toast.id != id));
  }

  private autoRemoveToastById(
    id: string,
    delay: number,
    animationDelay: number,
  ) {
    const markAsClosed$ = timer(delay).pipe(
      map(() => this.markToastAsClosed(id)),
    );
    const remove$ = timer(animationDelay).pipe(
      map(() => this.removeToastById(id)),
    );
    firstValueFrom(markAsClosed$.pipe(concatMap(() => remove$)));
  }

  private showToast(settings: IToastSettings) {
    let finalSettings = { ...(this.config ?? {}), ...settings };

    const id = this.createNewId();
    const trackableToast: TToast = {
      id,
      closed: false,
      settings: finalSettings,
    };
    if (finalSettings?.useDynamicInjection) {
      trackableToast.cmpRef = this.createToast(finalSettings);
      trackableToast.cmpRef.instance.click = () => {
        trackableToast.settings.clickFn?.(undefined);
        this.removeToastById(trackableToast.id);
      };
    }
    this._toasts.update((toasts) => {
      return [trackableToast, ...toasts];
    });

    if (!settings.pinned) {
      const delay = settings.hideDelay ?? ToasterDelay.DEFAULT;
      const animationDelay =
        settings.closeAnimationDelay ?? CloseAnimationDelay.DEFAULT;
      this.autoRemoveToastById(id, delay, animationDelay);
    }
  }

  private createToast(settings: IToastSettings): ComponentRef<ToastComponent> {
    const toastCmp = createComponent(ToastComponent, {
      environmentInjector: this.appRef.injector,
    });
    toastCmp.setInput('settings', settings);

    this.calculateToastPosition(toastCmp);

    this.appRef.attachView(toastCmp.hostView);

    document.body.firstElementChild?.appendChild(
      toastCmp.location.nativeElement,
    );
    return toastCmp;
  }

  private calculateToastPosition(toastCmpRef: ComponentRef<ToastComponent>) {
    const position = this.config?.position ?? ToastPositionEnum.BottomRight;
    const height = window
      .getComputedStyle(document.body)
      .getPropertyValue('--spacing-toast-height');
    const margin = window
      .getComputedStyle(document.body)
      .getPropertyValue('--spacing-toast-margin');
    let heightValue = height.includes('rem')
      ? +height.replace('rem', '') * 16
      : +height.replace('px', '');
    let marginValue = margin.includes('rem')
      ? +margin.replace('rem', '') * 16
      : +margin.replace('px', '');

    let toastHOffset =
      marginValue + (marginValue + heightValue) * this.toasts().length;

    let style: string = '';

    switch (position) {
      case ToastPositionEnum.TopLeft:
        style = `top: ${toastHOffset - heightValue}px; left: ${marginValue}px;`;
        break;
      case ToastPositionEnum.TopRight:
        style = `top: ${toastHOffset - heightValue}px; right: ${marginValue}px;`;
        break;
      case ToastPositionEnum.BottomLeft:
        style = `bottom: ${toastHOffset}px; left: ${marginValue}px;`;
        break;
      default:
        style = `bottom: ${toastHOffset}px; right: ${marginValue}px;`;
        break;
    }
    toastCmpRef.location.nativeElement.setAttribute('style', style);
  }

  //#endregion
}
